import React, { useMemo, useState } from 'react';
import { useHistory } from 'react-router';

import { Box, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  EventNote, ExpandLess, ExpandMore, NotificationsActiveOutlined, QueryBuilder, VideocamOutlined
} from '@mui/icons-material';
import { CLIENT_STATUSES, TimeFormats } from '@uplift/constants';
import { useTimezone } from '@uplift/contexts';
import { trackStartSessionClick, useL10n } from '@uplift/hooks';
import { CalendarDateService } from '@uplift/services';
import { UserRole } from '@uplift/types';
import {
  Alert, Button, LightChip, Loader, ProviderCancelSessionModal, SessionDetailsModal,
  StackableCard
} from '@uplift/ui-v2';
import { formatUserFullName, getUserFullName } from '@uplift/utils';

import { AddendumCorrectionSections } from '../../../../components/addendum-corrections';
import { TodaysAppointmentWithPatient } from '../hooks/use-todays-appointments.hook';
import { AppointmentReminders } from './AppointmentReminders.component';

export const useStyles = makeStyles({ name: 'TodaySessionItem' })((theme) => ({
  root: {
    padding: theme.spacing(3, 3),
  },
  content: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),

    [theme.breakpoints.up('sm')]: {
      flexDirection: 'row',
      gap: theme.spacing(0),
    }
  },
  details: {
    display: 'flex',
    flex: 1,
    flexDirection: 'column',
    gap: '4px',
  },
  detailsPatientName: {
    fontSize: '18px',
  },
  detailsTime: {
    maxWidth: '128px',
    flex: 1,
  },

  actions: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    alignItems: 'center',
    minWidth: '144px',
  },
  dotSeparator: {
    display: 'flex',
    height: '18px',
  },
  underlinedAction: {
    cursor: 'pointer',
    position: 'relative',
    userSelect: 'none',

    '&:after': {
      content: '""',
      position: 'absolute',
      bottom: '0.3rem',
      left: 0,
      width: '100%',
      height: '1px',
      backgroundColor: '#000',
    }
  },
  remindersButton: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    cursor: 'pointer',
    userSelect: 'none',
  }
}));


export type TodaySessionItemProps = {
  refreshOnUpdate: () => void;
  sessionCount: number;
  appointmentWithPatient: TodaysAppointmentWithPatient;
};


export const TodaySessionItem: React.FC<TodaySessionItemProps> = ({
  appointmentWithPatient: { appointment, patient },
  sessionCount,
  refreshOnUpdate,
}) => {
  const l10n = useL10n();
  const { classes } = useStyles();
  const timezone = useTimezone();
  const history = useHistory();
  const [ remindersShown, setRemindersShown ] = React.useState(false);
  const time = CalendarDateService.date(appointment.fromDatetime).tz(timezone).format(TimeFormats.LT);
  const [ showDetailsModal, setShowDetailsModal ] = useState(false);
  const [ isVisibleCancelSessionModal, setIsVisibleCancelSessionModal ] = useState(false);
  const isStartSessionAvailable = React.useMemo(() => {
    const isPatientRegular = patient?.status === CLIENT_STATUSES.regular;
    const isFinished2hoursAgo = CalendarDateService.date().diff(appointment.toDatetime, 'hours') >= 2;
    const isSessionScheduled = appointment.state === 'scheduled' || appointment.state === 'initial';
    const isSessionStarted = appointment.state === 'started' && !isFinished2hoursAgo;
    const isExpired = isSessionScheduled && isFinished2hoursAgo;
    const startIn30Minutes = CalendarDateService.date(appointment.fromDatetime).diff(CalendarDateService.date(), 'minutes') <= 30;

    if (!isPatientRegular) {
      return null;
    }

    if (isExpired) {
      return null;
    }

    if (isSessionStarted) {
      return 'sessionInProgress';
    }

    if (isSessionScheduled && startIn30Minutes) {
      return 'sessionReadyToStart';
    }

    return null;
  }, [ appointment, patient ]);

  const isRegularClient = patient.status === 'regular';
  const isFirstSession = isRegularClient && sessionCount <= 1;

  const onCancelSessionSubmit = async () => {
    refreshOnUpdate();
  };

  const patientName = useMemo(() => formatUserFullName({
    firstName: patient.firstName,
    lastName: patient.lastName,
    preferredFirstName: patient?.preferredName,
  }), [ patient.firstName, patient.lastName, patient?.preferredName ]);

  const patientDisplayName = useMemo(
    () => getUserFullName(patient.firstName, patient.lastName, patient.preferredName),
    [ patient.firstName, patient.lastName, patient.preferredName ]
  );

  const linkSession = `/appointments/${appointment.id}/session`;

  const handleStartSession = () => {
    trackStartSessionClick({ userType: UserRole.provider });
    history.push(linkSession);
  };

  return (
    <StackableCard className={classes.root}>
      <Box className={classes.content}>
        <Box className={classes.details}>
          <Box display="flex" gap="8px">
            <Typography variant="h6" className={classes.detailsPatientName}>{patientName}</Typography>
            {isFirstSession && <LightChip label="First session" size="small" color="info" />}
            {!isRegularClient && <LightChip label="Signup Incomplete" size="small" color="warning" />}
          </Box>

          <Box display="flex" gap="8px" alignItems="center">
            <QueryBuilder fontSize="small" color="action" />
            <Typography className={classes.detailsTime}>{time}</Typography>
          </Box>

          <Box display="flex" gap="8px" alignItems="center">
            <EventNote fontSize="small" color="action" />
            <Box display="flex" alignItems="center" gap="4px">
              <Typography className={classes.underlinedAction} onClick={() => setShowDetailsModal(true)}>Session details</Typography>
              <span className={classes.dotSeparator}>•</span>
              <Box
                className={classes.remindersButton}
                onClick={() => setRemindersShown(!remindersShown)}
              >
                <NotificationsActiveOutlined fontSize="small" color="action" />
                <Typography>
                  Reminders
                </Typography>
                {remindersShown ? <ExpandLess fontSize="inherit" /> : <ExpandMore fontSize="inherit" />}
              </Box>
            </Box>
          </Box>
        </Box>

        <Box className={classes.actions}>
          <Button
            variant="contained"
            color="secondary"
            startIcon={<VideocamOutlined />}
            onClick={handleStartSession}
            disabled={!isStartSessionAvailable}
            fullWidth
          >
            {l10n.sessions.start}
          </Button>
        </Box>
      </Box>

      {!isRegularClient && (
        <Alert showIcon={false} severity="warning" style={{ marginTop: 12 }}>
          <Typography variant="body2" color="textPrimary">
            {l10n.signupIncomplete.details}
          </Typography>
        </Alert>
      )}

      {remindersShown && (
        <Box pt="12px">
          <Box p={2} bgcolor="grey.100">
            <Box p={2} bgcolor="common.white">
              <React.Suspense fallback={<Loader height="48px" color="secondary" />}>
                <AppointmentReminders appointmentId={appointment.id} />
              </React.Suspense>
            </Box>
          </Box>
        </Box>
      )}

      {isVisibleCancelSessionModal && (
        <ProviderCancelSessionModal
          open
          fromDatetime={appointment?.fromDatetime}
          appointmentId={appointment?.id}
          parentId={appointment?.appointmentSeriesId}
          onSubmit={onCancelSessionSubmit}
          onClose={() => {
            setIsVisibleCancelSessionModal(false);
          }}
          patientDisplayName={patientDisplayName}
          patientId={appointment?.patientId}
          patientStatus={patient?.status}
        />
      )}

      <SessionDetailsModal
        open={showDetailsModal}
        appointment={{
          id: appointment.id,
          parentId: appointment?.appointmentSeriesId,
          fromDatetime: appointment?.fromDatetime,
          isMaterialized: appointment?.isMaterialized,
        }}
        onCancelSession={() => {
          setShowDetailsModal(false);
          setIsVisibleCancelSessionModal(true);
        }}
        onClose={() => setShowDetailsModal(false)}
        keepMounted={false}
        correctionSections={(ctx) => <AddendumCorrectionSections {...ctx} />}
      />
    </StackableCard>
  );
};
